import type { CSSProperties, JSX } from 'react';
import { TextField } from '../../../../_components/TextField.js';
import type { Palette } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';

interface AddressFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: Record<string, string>) => void;
}

type AddressFieldKey = 'street' | 'city' | 'state' | 'zip' | 'country';

interface AddressValue {
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

const FIELD_CONFIG: Record<AddressFieldKey, { label: string; placeholder: string; span: 1 | 2 }> = {
  street: { label: 'Street address', placeholder: '123 Main St', span: 2 },
  city: { label: 'City', placeholder: 'San Francisco', span: 1 },
  state: { label: 'State / Province', placeholder: 'CA', span: 1 },
  zip: { label: 'ZIP / Postal code', placeholder: '94102', span: 1 },
  country: { label: 'Country', placeholder: 'United States', span: 1 },
};

const DEFAULT_FIELDS: AddressFieldKey[] = ['street', 'city', 'state', 'zip', 'country'];

// Address grid — survey author can choose which fields to render via
// `settings.addressFields`. Defaults to the full set. 2-col grid on desktop;
// stacks naturally on narrow screens because each field spans by column count.
export function AddressField({ palette, question, value, onChange }: AddressFieldProps): JSX.Element {
  const current: AddressValue = (value as AddressValue) ?? {};
  const fields = (question.settings?.addressFields?.length
    ? question.settings.addressFields
    : DEFAULT_FIELDS) as AddressFieldKey[];

  const update = (patch: Partial<AddressValue>): void => {
    onChange({ ...current, ...patch } as Record<string, string>);
  };

  return (
    <div style={gridStyle()}>
      {fields.map((field) => {
        const config = FIELD_CONFIG[field];
        if (!config) return null;
        return (
          <div key={field} style={{ gridColumn: `span ${config.span}` }}>
            <label style={labelStyle(palette)}>{config.label}</label>
            <TextField
              palette={palette}
              value={current[field] ?? ''}
              onChange={(next) => update({ [field]: next } as Partial<AddressValue>)}
              placeholder={config.placeholder}
              ariaLabel={config.label}
            />
          </div>
        );
      })}
    </div>
  );
}

function gridStyle(): CSSProperties {
  return { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 };
}

function labelStyle(palette: Palette): CSSProperties {
  return {
    display: 'block',
    fontSize: 12,
    fontWeight: 500,
    color: palette.textMuted,
    marginBottom: 4,
  };
}
